import { useState, type FormEvent } from "react";
import FadeIn from "@/components/base/FadeIn";

export default function UpcomingShow() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    artist: "",
    placement: "",
    message: "",
  });
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setStatus("error");
      return;
    }
    setStatus("loading");
    try {
      const body = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        artist: formData.artist,
        placement: formData.placement,
        message: formData.message,
      });
      const res = await fetch("https://readdy.ai/api/form/d8qnjogn4gr6p3b2ga3g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus("success");
        setFormData({ name: "", email: "", artist: "", placement: "", message: "" });
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const artists = ["Marcus Aurelius — Classical Realism", "Isabella Rossi — Sacred Geometry", "Cassius Vel — Chiaroscuro", "Seraphina Dor — Fine Line & Gold Accent"];

  return (
    <section id="booking" className="relative bg-background-50 py-24 md:py-36 overflow-hidden">
      {/* Background image */}
      <FadeIn className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Luxury%20tattoo%20studio%20interior%20with%20ivory%20marble%20counters%20walnut%20slat%20paneling%20warm%20amber%20museum%20lighting%20classical%20Roman%20columns%20and%20fine%20art%20gold%20frames%20on%20terracotta%20walls%20serene%20contemplative%20gallery%20atmosphere%20soft%20golden%20glow%20architectural%20details&width=1920&height=1080&seq=booking-bg-001&orientation=landscape"
          alt="Booking background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/75 via-foreground-950/45 to-foreground-950/25"></div>
      </FadeIn>

      <div className="relative px-6 md:px-12 max-w-5xl">
        <p className="font-display text-xs uppercase tracking-[0.25em] text-background-50/80 mb-6">
          ( Booking )
        </p>
        <h2 className="font-display font-black text-background-50 leading-[0.85] tracking-[-0.04em] text-[16vw] md:text-[11vw] lg:text-[9vw] mb-8">
          BEGIN YOUR <em className="font-serif italic font-light">journey</em>
        </h2>
        <p className="font-display text-xl md:text-3xl text-background-50/95 uppercase tracking-tight mb-8 font-medium">
          Consultation Request
        </p>
        <p className="text-background-50/85 text-base md:text-lg leading-relaxed font-serif max-w-2xl mb-10">
          Every piece begins with a conversation. Share your vision and our curators will match you with the artist whose tradition best aligns with your story. Consultations are held in our Cranford gallery.
        </p>

        <form
          id="booking-form-tattoo-collective"
          data-readdy-form
          onSubmit={handleSubmit}
          className="max-w-2xl pt-8 border-t border-background-50/25"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div>
              <label className="block font-display text-[10px] uppercase tracking-[0.2em] text-background-50/60 mb-2">Full Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full bg-transparent border-b border-background-50/40 focus:border-accent-500 outline-none text-background-50 placeholder:text-background-50/30 px-1 py-3 text-sm transition-colors"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="block font-display text-[10px] uppercase tracking-[0.2em] text-background-50/60 mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full bg-transparent border-b border-background-50/40 focus:border-accent-500 outline-none text-background-50 placeholder:text-background-50/30 px-1 py-3 text-sm transition-colors"
                placeholder="your@email.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div>
              <label className="block font-display text-[10px] uppercase tracking-[0.2em] text-background-50/60 mb-2">Preferred Artist</label>
              <select
                name="artist"
                value={formData.artist}
                onChange={(e) => setFormData({ ...formData, artist: e.target.value })}
                className="w-full bg-transparent border-b border-background-50/40 focus:border-accent-500 outline-none text-background-50 px-1 py-3 text-sm transition-colors cursor-pointer"
              >
                <option value="" className="text-foreground-950">Select an artist</option>
                {artists.map((a) => (
                  <option key={a} value={a} className="text-foreground-950">{a}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-display text-[10px] uppercase tracking-[0.2em] text-background-50/60 mb-2">Placement</label>
              <input
                type="text"
                name="placement"
                value={formData.placement}
                onChange={(e) => setFormData({ ...formData, placement: e.target.value })}
                className="w-full bg-transparent border-b border-background-50/40 focus:border-accent-500 outline-none text-background-50 placeholder:text-background-50/30 px-1 py-3 text-sm transition-colors"
                placeholder="e.g. forearm, back, chest"
              />
            </div>
          </div>

          <div className="mb-8">
            <label className="block font-display text-[10px] uppercase tracking-[0.2em] text-background-50/60 mb-2">Describe Your Vision</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              maxLength={500}
              rows={3}
              className="w-full bg-transparent border-b border-background-50/40 focus:border-accent-500 outline-none text-background-50 placeholder:text-background-50/30 px-1 py-3 text-sm transition-colors resize-none"
              placeholder="Tell us about the piece you envision..."
            />
            <p className="text-[10px] text-background-50/40 mt-1 font-display uppercase tracking-[0.15em]">{formData.message.length}/500</p>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10">
            <button
              type="submit"
              disabled={status === "loading"}
              className="inline-flex items-center gap-3 px-8 py-4 bg-accent-500 text-foreground-950 font-display text-sm uppercase tracking-[0.18em] rounded-full hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
            >
              {status === "loading" ? "Sending..." : "Request Consultation"}
              <i className="ri-arrow-right-up-line"></i>
            </button>
            {status === "success" && (
              <p className="text-accent-400 text-sm font-serif italic">Thank you — we&apos;ll be in touch within 48 hours.</p>
            )}
            {status === "error" && (
              <p className="text-red-400 text-sm font-serif italic">Please check your email and try again.</p>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}